import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { CONTACT } from "@/data/site";
import { EASE } from "@/components/motion";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/about", label: "About", id: "about" },
  { to: "/products", label: "Minerals", id: "products" },
  { to: "/services", label: "Services", id: "services" },
  { to: "/contact", label: "Contact", id: "contact" },
];

const Brand = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
    <img
      src="/assets/tg-icon.png"
      alt="Tuskers Grey monogram"
      className="h-10 w-auto transition-transform duration-500 group-hover:scale-105"
    />
    <span className="leading-none">
      <span className="block font-serif text-base tracking-[0.18em] text-slate-100">
        TUSKERS <span className="text-gold">GREY</span>
      </span>
      <span className="mt-1 block text-[10px] uppercase tracking-[0.42em] text-gold/80">
        Investments
      </span>
    </span>
  </Link>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || open
          ? "bg-navy-950/85 backdrop-blur-xl border-b hairline-gold"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <Brand />
        <nav className="hidden items-center gap-9 lg:flex" data-testid="nav-links">
          {LINKS.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `group relative text-[13px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                  isActive ? "text-gold" : "text-slate-300 hover:text-slate-50"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute -bottom-1.5 left-0 h-px bg-gold transition-[width] duration-500 ${
                      isActive ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a
            href={CONTACT.phones[0].tel}
            data-testid="nav-call-button"
            className="hidden items-center gap-2 rounded-full border border-gold/40 px-5 py-2.5 text-[12px] uppercase tracking-[0.2em] text-gold transition-all duration-300 hover:bg-gold hover:text-navy-950 lg:flex"
          >
            <Phone className="h-3.5 w-3.5" />
            Call us
          </a>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-slate-200 lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="border-t hairline-gold bg-navy-950/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col px-6 py-6">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.45, ease: EASE }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`nav-mobile-link-${l.id}`}
                    className={({ isActive }) =>
                      `block border-b border-white/5 py-4 font-serif text-2xl ${
                        isActive ? "text-gold" : "text-slate-200"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <a
                href={CONTACT.phones[0].tel}
                data-testid="nav-mobile-call-button"
                className="mt-6 flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-xs uppercase tracking-[0.25em] text-navy-950"
              >
                <Phone className="h-4 w-4" /> Call {CONTACT.phones[0].label}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
